import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import AppLoader from '../components/AppLoader'
import { useLang } from '../App'

const PublicLoaderContext = createContext(null)

export function PublicLoaderProvider({ children }) {
  const location = useLocation()
  const { lang } = useLang()
  
  // Track the primary route path (ignoring query strings)
  const currentPath = location.pathname
  const currentPathRef = useRef(currentPath)

  const [isReady, setIsReady] = useState(false)
  const [activePath, setActivePath] = useState(currentPath)

  useEffect(() => {
    if (currentPath !== currentPathRef.current) {
      currentPathRef.current = currentPath
      setIsReady(false)
      setActivePath(currentPath)
    }
  }, [currentPath])

  const setPageReady = useCallback((forPath) => {
    // Only accept readiness from the currently active route path
    if (forPath === currentPathRef.current) {
      setIsReady(true)
    }
  }, [])

  const value = useMemo(() => ({
    setPageReady,
    isReady,
    currentPath: activePath
  }), [isReady, setPageReady, activePath])

  return (
    <PublicLoaderContext.Provider value={value}>
      {!isReady && (
        <div className="fixed inset-0 z-[9999] bg-white">
          <AppLoader fullscreen={true} lang={lang} />
        </div>
      )}
      {/* Hide the page from screen readers and layout shifts while loading */}
      <div 
        aria-hidden={!isReady}
        style={{ 
          opacity: isReady ? 1 : 0, 
          transition: 'opacity 0.4s ease-in-out',
          pointerEvents: isReady ? 'auto' : 'none',
          height: isReady ? 'auto' : '100vh',
          overflow: isReady ? 'visible' : 'hidden'
        }}
      >
        {children}
      </div>
    </PublicLoaderContext.Provider>
  )
}

export const usePublicLoader = () => {
  const context = useContext(PublicLoaderContext)
  if (!context) {
    throw new Error('usePublicLoader must be used inside PublicLoaderProvider')
  }
  return context
}
